import {useMemo} from 'react'
import {Link, Navigate, useParams, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {isAxiosError} from 'axios'
import {KairosMark} from '@/components/kairos-mark'
import {Skeleton} from '@/components/ui/skeleton'
import {SegmentedControl, type SegmentedOption} from '@/components/segmented-control'
import {ListEmptyState} from '@/components/section-card'
import {Button} from '@/components/ui/button'
import {ResultadosKpis} from '@/components/bateria/resultados-kpis'
import {FichaTecnica} from '@/components/bateria/informe/ficha-tecnica'
import {RepartoNivelDonut} from '@/components/bateria/informe/reparto-nivel-donut'
import {DominiosBarras, DimensionesApiladas} from '@/components/bateria/informe/dominios-dimensiones'
import {CortesSociodemograficos} from '@/components/bateria/informe/cortes-sociodemograficos'
import {ValidezConfiabilidad} from '@/components/bateria/informe/validez-confiabilidad'
import {PlanIntervencion} from '@/components/bateria/informe/plan-intervencion'
import {PeriodicidadYProtocolos, Firma} from '@/components/bateria/informe/cierre-informe'
import {ReporteAcciones} from '@/components/bateria/informe/reporte-acciones'
import {useIsSystemUser} from '@/hooks/empresa/organizacion/use-businesses'
import {
  useCuarentenaCount,
  useEvaluacionDetail,
  useRevisionResumen,
  type BateriaReporteAlcance,
} from '@/hooks/bateria/use-bateria'
import {useReporteDatos, type ReporteDatos} from '@/hooks/bateria/use-bateria-informe'
import {agregadoDeFormaResumen} from '@/lib/bateria'

const ALCANCES: BateriaReporteAlcance[] = ['consolidado', 'A', 'B']

function reporteDatos409Motivo(error: unknown): string | null {
  if (!isAxiosError(error) || error.response?.status !== 409) return null
  const detail = (error.response.data as {detail?: {motivo?: string}} | undefined)?.detail
  return detail?.motivo ?? null
}

/** Solo formatea `reporte-datos`, nunca calcula: un número que difiera del DOCX es un bug. */
export function AdminBateriaInformePage() {
  const {t} = useTranslation()
  const {businessId, evaluacionId} = useParams<{businessId: string; evaluacionId: string}>()
  const isSystemUser = useIsSystemUser()
  const [searchParams, setSearchParams] = useSearchParams()
  const alcanceParam = searchParams.get('alcance')
  const alcance: BateriaReporteAlcance = ALCANCES.includes(alcanceParam as BateriaReporteAlcance)
    ? (alcanceParam as BateriaReporteAlcance)
    : 'consolidado'

  function setAlcance(next: BateriaReporteAlcance) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        params.set('alcance', next)
        return params
      },
      {replace: true}
    )
  }

  const {data: detail} = useEvaluacionDetail(businessId, evaluacionId, isSystemUser)
  const {
    data: reporteDatos,
    error: reporteError,
    isLoading: reporteLoading,
  } = useReporteDatos(businessId, evaluacionId, alcance, isSystemUser && !!businessId && !!evaluacionId)
  const cuarentenaPendiente = useCuarentenaCount(businessId, evaluacionId, isSystemUser, false)
  const {data: resumenRevision} = useRevisionResumen(businessId, evaluacionId, isSystemUser)
  const dudasPendientes = resumenRevision?.pendientes ?? 0

  const paquetes = useMemo(() => detail?.paquetes ?? [], [detail])
  const validadosPorForma = useMemo(() => {
    const contar = (forma: 'A' | 'B') => paquetes.filter((p) => p.forma === forma && p.estado === 'validado').length
    return {A: contar('A'), B: contar('B'), total: paquetes.filter((p) => p.estado === 'validado').length}
  }, [paquetes])

  if (!isSystemUser || !businessId || !evaluacionId) return <Navigate to="/" replace />

  const lotePath = `/admin/bateria/empresas/${businessId}/evaluaciones/${evaluacionId}`
  const motivo409 = reporteDatos409Motivo(reporteError)

  const opciones: SegmentedOption<BateriaReporteAlcance>[] = [
    {
      key: 'consolidado',
      label: t('admin.bateria.informe.alcance.consolidado', {count: validadosPorForma.total}),
      testId: 'informe-alcance-consolidado',
    },
    {
      key: 'A',
      label: t('admin.bateria.formaValue', {forma: 'A'}) + ` (${validadosPorForma.A})`,
      testId: 'informe-alcance-a',
    },
    {
      key: 'B',
      label: t('admin.bateria.formaValue', {forma: 'B'}) + ` (${validadosPorForma.B})`,
      testId: 'informe-alcance-b',
    },
  ]

  return (
    <div className="space-y-5" data-testid="admin-bateria-informe">
      <div>
        <Link to={lotePath} className="font-mono text-[11px] text-primary hover:underline">
          {t('admin.bateria.informe.back')}
        </Link>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-foreground">
            <KairosMark />
            <h1 className="font-heading text-xl">{t('admin.bateria.informe.title')}</h1>
          </div>
          {detail && (
            <ReporteAcciones
              businessId={businessId}
              evaluacionId={evaluacionId}
              evaluacion={detail.evaluacion}
              alcance={alcance}
            />
          )}
        </div>
      </div>

      {/* Cada alcance pide su agregado al servidor; nunca se filtra el consolidado en el cliente. */}
      <div className="sticky top-0 z-10 -mx-4 bg-background px-4 py-2 md:static md:mx-0 md:bg-transparent md:px-0 md:py-0">
        <SegmentedControl
          options={opciones}
          value={alcance}
          onChange={setAlcance}
          testId="informe-alcance-selector"
          ariaLabel={t('admin.bateria.informe.alcance.label')}
        />
      </div>

      {!detail ? (
        <Skeleton className="h-48 w-full" />
      ) : motivo409 === 'sin_validados' ? (
        <div className="surface" data-testid="informe-sin-validados">
          <ListEmptyState
            page
            title={t('admin.bateria.informe.sinValidados.title')}
            action={
              (dudasPendientes > 0 || cuarentenaPendiente > 0) && (
                <Button asChild size="sm" variant="outline" data-testid="informe-ir-a-cola">
                  <Link to={cuarentenaPendiente > 0 ? `${lotePath}/revision?ir_a=ubicar` : `${lotePath}/revision`}>
                    {t('admin.bateria.informe.sinValidados.irACola', {
                      dudas: dudasPendientes,
                      cuarentena: cuarentenaPendiente,
                    })}
                  </Link>
                </Button>
              )
            }
          />
        </div>
      ) : reporteError && !motivo409 ? (
        <div className="surface flex flex-col items-center gap-2 px-6 py-10 text-center" data-testid="informe-error">
          <p className="text-sm text-foreground">{t('admin.bateria.informe.errorGeneracion')}</p>
        </div>
      ) : reporteLoading || !reporteDatos ? (
        <div className="space-y-5">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : (
        <InformeContenido datos={reporteDatos} totalPaquetes={paquetes.length} />
      )}
    </div>
  )
}

function InformeContenido({datos, totalPaquetes}: {datos: ReporteDatos; totalPaquetes: number}) {
  const {t} = useTranslation()
  const consolidado = datos.consolidado
  const bajoAnonimato = consolidado !== null && !consolidado.suficiente

  return (
    <div className="space-y-5">
      <FichaTecnica datos={datos} />

      {bajoAnonimato && (
        <p className="notice-amber rounded-lg px-3.5 py-2.5 text-xs leading-snug" data-testid="informe-n-bajo">
          {t('admin.bateria.informe.nBajo', {count: consolidado?.n_validados ?? 0})}
        </p>
      )}

      {consolidado && (
        <ResultadosKpis
          consolidado={agregadoDeFormaResumen(consolidado)}
          validados={datos.n_total_validados}
          totalPaquetes={totalPaquetes}
        />
      )}

      {consolidado && (
        <RepartoNivelDonut cuestionarios={consolidado.cuestionarios} totalGeneral={consolidado.total_general} />
      )}
      {consolidado && <DominiosBarras dominios={consolidado.dominios} />}
      {consolidado && <DimensionesApiladas dimensiones={consolidado.dimensiones} />}

      <CortesSociodemograficos cortes={datos.sociodemografico} />
      <ValidezConfiabilidad validez={datos.validez} />
      <PlanIntervencion
        factoresPriorizados={datos.factores_priorizados}
        factoresMonitoreo={datos.factores_monitoreo}
        estresPriorizado={datos.estres_priorizado}
        planValidacionLine={datos.plan_validacion_line}
      />
      <PeriodicidadYProtocolos
        periodicidad={datos.periodicidad}
        articulo3={datos.articulo_3_periodicidad}
        protocoloGeneral={datos.protocolo_general}
        factoresConProtocolo={[...datos.factores_priorizados, ...datos.estres_priorizado]}
      />
      <Firma profesional={datos.profesional} />
    </div>
  )
}
